import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { getProperty } from '../lib/property-config.js';
import { getGuide } from '../lib/welcome-guides.js';
import Footer from '../components/Footer.jsx';
import Header from '../components/Header.jsx';
import NotFound from '../components/NotFound.jsx';
import { Eyebrow } from '../components/Section.jsx';
import Seo from '../components/Seo.jsx';

// The host's welcome guide, rendered from welcome-guides.js. Public page:
// codes, passwords, and the street address are never in the data (see the
// privacy rule at the top of welcome-guides.js).

function GuideSection({ kicker, title, children }) {
  return (
    <section className="border-t border-navy-100 py-6">
      <Eyebrow text={kicker} />
      <h2 className="mb-3 font-display text-xl font-semibold text-navy-900 sm:text-2xl">
        {title}
      </h2>
      <div className="text-navy-800">{children}</div>
    </section>
  );
}

// Guests staying in Mount Pleasant read geography backyard-out: their side
// of the harbor first, then the beaches, then across the Ravenel.
const AREA_ORDER = [
  'Mount Pleasant',
  'Shem Creek',
  "Sullivan's Island",
  'Isle of Palms',
  'Downtown'
];

function groupByArea(places) {
  const groups = new Map();
  for (const p of places) {
    if (!groups.has(p.area)) groups.set(p.area, []);
    groups.get(p.area).push(p);
  }
  return [...groups.entries()].sort(([a], [b]) => {
    const ai = AREA_ORDER.indexOf(a);
    const bi = AREA_ORDER.indexOf(b);
    return (ai === -1 ? AREA_ORDER.length : ai) - (bi === -1 ? AREA_ORDER.length : bi);
  });
}

// One collapsible category row: closed it's a scannable line with a count,
// open it's the places grouped by area.
function GuideCategory({ category, places, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-navy-100">
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 py-3 text-left"
      >
        <span className="font-display text-base font-semibold text-navy-900">{category}</span>
        <span className="flex shrink-0 items-center gap-2 text-xs text-navy-500">
          {places.length} {places.length === 1 ? 'spot' : 'spots'}
          <ChevronDown
            className={['h-4 w-4 transition-transform', open ? 'rotate-180' : ''].join(' ')}
          />
        </span>
      </button>
      {open && (
        <div className="space-y-3 pb-4">
          {groupByArea(places).map(([area, list]) => (
            <div key={area}>
              <p className="text-xs font-medium uppercase tracking-[0.14em] text-cream-600">
                {area}
              </p>
              <ul className="mt-1 space-y-1 text-sm leading-relaxed">
                {list.map((p) => (
                  <li key={p.name}>
                    <span className="font-semibold text-navy-900">{p.name}</span>
                    {p.note && <span className="text-navy-700"> · {p.note}</span>}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function StayGuide() {
  const { slug } = useParams();
  const property = getProperty(slug);
  const guide = getGuide(slug);

  if (!property || !guide) {
    return (
      <NotFound
        title="No guide here."
        seoTitle="Guide not found · Charleston.Vacations"
      >
        <Link to="/" className="text-navy-700 underline">Back to today's brief</Link>
      </NotFound>
    );
  }

  return (
    <div className="mx-auto max-w-brief px-4 pt-6 sm:px-6">
      <Seo
        title={`Welcome Guide · ${property.name} · Charleston.Vacations`}
        description={`The hosts' welcome guide for ${property.name} in ${property.location}: the home, house rules, and the places ${guide.hosts} actually recommend.`}
        noindex
      />
      <Header />
      {/* The article runs at the narrower long-form measure while the masthead
          stays at `brief` width, so the header keeps matching every other page. */}
      <article className="mx-auto max-w-read">
        <div className="mt-8 border-b border-navy-100 pb-6">
          <h1 className="font-display text-3xl font-semibold text-navy-900 sm:text-4xl">
            Welcome Guide
          </h1>
          <p className="mt-2 text-navy-600">
            {property.name} · {property.location} · hosted by {guide.hosts}
          </p>
          <p className="mt-4 font-serif leading-relaxed text-navy-800">{guide.intro}</p>
          <p className="mt-3 text-sm text-navy-500">
            <Link to={`/stay/${property.slug}`} className="text-navy-700 underline">
              Check availability and book direct <span aria-hidden="true">›</span>
            </Link>
          </p>
        </div>

        <GuideSection kicker="At a glance" title="The essentials">
          <dl className="space-y-3 text-sm">
            {guide.essentials.map((e) => (
              <div key={e.label} className="sm:flex sm:gap-4">
                <dt className="w-28 shrink-0 text-xs font-medium uppercase tracking-wider text-navy-500 sm:pt-0.5">
                  {e.label}
                </dt>
                <dd className="leading-relaxed">{e.value}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-4 rounded-lg border border-cream-300 bg-cream-100 px-4 py-3 text-xs leading-relaxed text-navy-600">
            For your security, door codes and Wi-Fi details are never published on this
            page. They arrive in your check-in message before arrival.
          </p>
        </GuideSection>

        <GuideSection kicker="The home" title="Rooms & amenities">
          <p className="mb-4 text-sm text-navy-600">
            {property.bedrooms} bedrooms · {property.bathrooms} baths · sleeps {property.sleeps}
          </p>
          <div className="space-y-4">
            {guide.rooms.map((room) => (
              <div key={room.name}>
                <h3 className="font-display text-base font-semibold text-navy-900">{room.name}</h3>
                <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm leading-relaxed text-navy-700">
                  {room.details.map((d, i) => (
                    <li key={i}>{d}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </GuideSection>

        <GuideSection kicker="Need something?" title="Where to find things">
          <div className="space-y-4">
            {guide.whereToFind.map((group) => (
              <div key={group.category}>
                <h3 className="font-display text-base font-semibold text-navy-900">
                  {group.category}
                </h3>
                <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm leading-relaxed text-navy-700">
                  {group.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </GuideSection>

        <GuideSection kicker="The house" title="House rules & checkout">
          <ul className="list-disc space-y-0.5 pl-5 text-sm leading-relaxed">
            {guide.houseRules.map((rule, i) => (
              <li key={i}>{rule}</li>
            ))}
          </ul>
          <p className="mt-3 text-sm leading-relaxed text-navy-700">{guide.cameras}</p>
          <h3 className="mt-5 font-display text-base font-semibold text-navy-900">
            Before you head out
          </h3>
          <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm leading-relaxed text-navy-700">
            {guide.checkoutList.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </GuideSection>

        <GuideSection kicker="From the hosts" title="Our local guide">
          <p className="mb-4 text-sm leading-relaxed text-navy-600">
            The spots we send friends to. For a read on which of these fits today's
            conditions, the <Link to="/" className="text-navy-700 underline">daily brief</Link>{' '}
            is written every morning by 6:30.
          </p>
          <p className="mb-2 mt-5 text-xs font-medium uppercase tracking-[0.18em] text-navy-500">
            Eat & drink
          </p>
          <div className="border-t border-navy-100">
            {guide.localGuide.map((group, i) => (
              <GuideCategory
                key={group.category}
                category={group.category}
                places={group.places}
                defaultOpen={i === 0}
              />
            ))}
          </div>
          <p className="mb-2 mt-7 text-xs font-medium uppercase tracking-[0.18em] text-navy-500">
            Out & about
          </p>
          <div className="border-t border-navy-100">
            {guide.activities.map((group) => (
              <GuideCategory key={group.category} category={group.category} places={group.places} />
            ))}
          </div>
        </GuideSection>

        <GuideSection kicker="Getting around" title="Where you are">
          <ul className="space-y-1.5 text-sm leading-relaxed">
            {guide.locationHighlights.map((h) => (
              <li key={h.place}>
                <span className="font-semibold text-navy-900">{h.place}</span>
                <span className="text-navy-700"> · {h.distance}</span>
              </li>
            ))}
          </ul>
        </GuideSection>

        <p className="border-t border-navy-100 py-6 text-center font-serif italic text-navy-600">
          We hope you have a wonderful stay. {guide.hosts}
        </p>
      </article>
      <Footer />
    </div>
  );
}
